// src/components/InventoryActionsBar.jsx
import React, { useState } from 'react';

export default function InventoryActionsBar({
  handleExportCsv,
  setShowRestoreModal,
  handleOpenManualTags,
  handleOpenHelp,
  selectedCount,
  selectedStatus,
  setSelectedStatus,
  handleStatusChange,
  handleOpenMoveModal,
  moveLoading,
  handleUndoLastEdit,
  selectedSaleId,
}) {
  const [sheetOpen, setSheetOpen] = useState(false);

  const applyStatus = (status) => {
    setSelectedStatus(status);
    handleStatusChange(status);
    setSheetOpen(false);
  };

  const runAndClose = (fn) => {
    fn();
    setSheetOpen(false);
  };

  return (
    <>
      {/* Desktop actions bar */}
      <div className="fixed bottom-0 left-0 right-0 z-20 hidden sm:block bg-white border-t border-gray-200 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
        <div className="flex flex-col sm:flex-row justify-between gap-2 px-4 py-3">
          <div className="flex items-center gap-2 w-full sm:w-auto">
        {/* Export CSV */}
        <button
          type="button"
          onClick={handleExportCsv}
          className="inline-flex items-center h-8 px-4 rounded-md text-sm font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-300 shadow-sm flex-shrink-0"
        >
          Export CSV
        </button>

        {/* Show Items On Hold */}
        <button
          type="button"
          onClick={() => setShowRestoreModal(true)}
          className="inline-flex items-center h-8 px-4 rounded-md text-sm font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-300 shadow-sm flex-shrink-0 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          Show Items On Hold
        </button>

        {/* Manual Tags */}
        <button
          type="button"
          onClick={handleOpenManualTags}
          disabled={!selectedSaleId}
          className="inline-flex items-center h-8 px-4 rounded-md text-sm font-medium bg-slate-50 text-slate-700 border border-slate-200 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300 shadow-sm flex-shrink-0 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          Manual Tags
        </button>

        {/* Status Dropdown (Remove Selected Items) */}
        <div className="flex flex-col gap-1">
          <select
            id="status-select"
            value={selectedStatus}
            onChange={(e) => {
              const newStatus = e.target.value;
              setSelectedStatus(newStatus);
              if (newStatus) {
                handleStatusChange(newStatus);
              }
            }}
            disabled={moveLoading || selectedCount === 0}
            className={`h-8 px-2 rounded-md text-sm font-medium shadow-sm border flex-shrink-0 ${
              selectedCount === 0
                ? 'bg-gray-200 text-gray-500 border-gray-300 cursor-not-allowed'
                : 'bg-rose-600 text-white border-rose-600 hover:bg-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-300'
            }`}
            aria-label="Set status for selected items"
          >
            <option value="" disabled>
              Remove Selected Items
            </option>
            <option value="ON HOLD">ON HOLD</option>
            <option value="DELETE">DELETE</option>
          </select>
        </div>

        {/* Move Selected Items */}
        <button
          type="button"
          onClick={handleOpenMoveModal}
          disabled={selectedCount === 0}
          className={`h-8 px-3 rounded-md text-sm font-medium shadow-sm border flex-shrink-0 ml-2 ${
            selectedCount === 0
              ? 'bg-gray-200 text-gray-500 border-gray-300 cursor-not-allowed'
              : 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-300'
          }`}
        >
          Move Selected Items
        </button>
        <button
          type="button"
          onClick={handleUndoLastEdit}
          disabled={!selectedSaleId}  // only disable if no sale selected
          className="inline-flex items-center h-8 px-4 rounded-md text-sm font-medium bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100 focus:outline-none focus:ring-2 focus:ring-orange-300 shadow-sm flex-shrink-0 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          Undo Last Edit
        </button>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            <button
              type="button"
              onClick={handleOpenHelp}
              className="inline-flex items-center h-8 px-4 rounded-md text-sm font-medium bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300 shadow-sm flex-shrink-0"
            >
              Help
            </button>
          </div>
        </div>
      </div>

      {/* Mobile actions bar */}
      <div className="fixed bottom-0 left-0 right-0 z-20 sm:hidden bg-white border-t border-gray-200 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="text-sm text-gray-600">
            {selectedCount > 0 ? `${selectedCount} selected` : 'No selection'}
          </div>
          <button
            type="button"
            onClick={() => setSheetOpen(true)}
            className="inline-flex items-center h-9 px-4 rounded-md text-sm font-medium bg-indigo-600 text-white border border-indigo-600 hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-300 shadow-sm"
          >
            Inventory Actions
          </button>
        </div>
      </div>

      {sheetOpen && (
        <>
          <div
            className="fixed inset-0 z-30 bg-black/40 sm:hidden"
            onClick={() => setSheetOpen(false)}
          />
          <div className="fixed inset-x-0 bottom-0 z-40 sm:hidden bg-white rounded-t-2xl shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-base font-semibold">Inventory Actions</h3>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="text-2xl leading-none text-gray-500"
              >
                &times;
              </button>
            </div>
            <div className="p-4 space-y-2">
              <button
                type="button"
                onClick={() => runAndClose(handleExportCsv)}
                className="w-full h-10 rounded-md text-sm font-medium bg-indigo-50 text-indigo-700 border border-indigo-200"
              >
                Export CSV
              </button>
              <button
                type="button"
                onClick={() => runAndClose(() => setShowRestoreModal(true))}
                className="w-full h-10 rounded-md text-sm font-medium bg-emerald-50 text-emerald-700 border border-emerald-200"
              >
                Show Items On Hold
              </button>
              <button
                type="button"
                onClick={() => runAndClose(handleOpenManualTags)}
                disabled={!selectedSaleId}
                className="w-full h-10 rounded-md text-sm font-medium bg-slate-50 text-slate-700 border border-slate-200 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Manual Tags
              </button>
              <button
                type="button"
                onClick={() => runAndClose(handleOpenMoveModal)}
                disabled={selectedCount === 0}
                className="w-full h-10 rounded-md text-sm font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Move Selected Items
              </button>
              <button
                type="button"
                onClick={() => runAndClose(handleUndoLastEdit)}
                disabled={!selectedSaleId}
                className="w-full h-10 rounded-md text-sm font-medium bg-orange-50 text-orange-700 border border-orange-200 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Undo Last Edit
              </button>
              <button
                type="button"
                onClick={() => runAndClose(handleOpenHelp)}
                className="w-full h-10 rounded-md text-sm font-medium bg-white text-slate-700 border border-slate-300"
              >
                Help
              </button>
              <div className="pt-2">
                <div className="text-xs font-semibold text-gray-500 mb-2">
                  Remove Selected Items
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => applyStatus('ON HOLD')}
                    disabled={moveLoading || selectedCount === 0}
                    className="h-9 rounded-md text-sm font-medium bg-amber-50 text-amber-700 border border-amber-200 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    ON HOLD
                  </button>
                  <button
                    type="button"
                    onClick={() => applyStatus('DELETE')}
                    disabled={moveLoading || selectedCount === 0}
                    className="h-9 rounded-md text-sm font-medium bg-rose-50 text-rose-700 border border-rose-200 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    DELETE
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
